/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MenuPermissionsService, MenuItemDef, MenuPolicy } from './menu-permissions.service';
import { PersonRef, WorkflowService } from '../workflow/workflow.service';

/**
 * The menu-visibility policy manager an administrator sees inside
 * Settings → administration.
 *
 * The list on top shows the built-in administrators policy — readable, never
 * editable or deletable — followed by every policy the administrator saved.
 * Picking a row opens its draft below: a name, the menus it opens, and the
 * people and groups it belongs to. Save writes the whole list back to the
 * repository document the service keeps.
 *
 * The rule the policies express is strict: a menu no policy names is hidden
 * from everyone but administrators.
 */
@Component({
  selector: 'app-menu-permissions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './menu-permissions.component.html',
  styleUrl: './menu-permissions.component.scss'
})
export class MenuPermissionsComponent implements OnInit {
  /** Whether the screen sits inside the settings page rather than on its own route. */
  @Input() embedded = false;

  readonly menuPerms = inject(MenuPermissionsService);
  private readonly wf = inject(WorkflowService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  /** Navigation entries first, then the settings-page sections. */
  readonly navMenus = MenuPermissionsService.MENU_ITEMS.filter((m) => m.kind === 'nav');
  readonly settingsMenus = MenuPermissionsService.MENU_ITEMS.filter((m) => m.kind === 'settings');
  readonly builtin = MenuPermissionsService.BUILTIN;

  /** The policy being edited in the draft below the list — null = list only. */
  readonly editing = signal<MenuPolicy | null>(null);
  readonly busy = signal(false);

  /** Display names remembered for chips — policies store ids only. */
  readonly labels = new Map<string, string>();

  userTerm = '';
  groupTerm = '';
  readonly userResults = signal<PersonRef[]>([]);
  readonly groupResults = signal<PersonRef[]>([]);

  ngOnInit(): void {
    this.menuPerms.load();
  }

  /** The rows the list shows — the system policy first, then the saved ones. */
  get rows(): MenuPolicy[] {
    return [this.builtin, ...this.menuPerms.policies()];
  }

  isBuiltin(policy: MenuPolicy): boolean {
    return policy.id === this.builtin.id;
  }

  select(policy: MenuPolicy): void {
    if (this.isBuiltin(policy)) {
      this.editing.set(null);
      return;
    }
    this.editing.set({ ...policy, users: [...policy.users], groups: [...policy.groups], menus: [...policy.menus] });
  }

  create(): void {
    this.editing.set({ id: `pol-${Date.now()}`, name: '', users: [], groups: [], menus: [] });
  }

  cancel(): void {
    this.editing.set(null);
  }

  isEditing(policy: MenuPolicy): boolean {
    return this.editing()?.id === policy.id;
  }

  /** Menu checklist — id is either granted by the draft or it is not. */
  hasMenu(menuId: string): boolean {
    return this.editing()?.menus.includes(menuId) ?? false;
  }

  toggleMenu(menuId: string, checked: boolean): void {
    this.editing.update((d) => (d ? { ...d, menus: checked ? [...new Set([...d.menus, menuId])] : d.menus.filter((m) => m !== menuId) } : d));
  }

  /** Grants or revokes a whole checklist group in one click. */
  setAllMenus(items: MenuItemDef[], on: boolean): void {
    const ids = items.map((m) => m.id);
    this.editing.update((d) => (d ? { ...d, menus: on ? [...new Set([...d.menus, ...ids])] : d.menus.filter((m) => !ids.includes(m)) } : d));
  }

  // ---- people & groups -------------------------------------------------

  searchUsers(term: string): void {
    this.wf.searchPeople(term).subscribe((r) => this.userResults.set(r.filter((p) => !this.editing()?.users.includes(p.id))));
  }

  searchGroups(term: string): void {
    this.wf.searchGroups(term).subscribe((r) => this.groupResults.set(r.filter((g) => !this.editing()?.groups.includes(g.id))));
  }

  addUser(person: PersonRef): void {
    this.labels.set(person.id, person.displayName);
    this.editing.update((d) => (d ? { ...d, users: [...new Set([...d.users, person.id])] } : d));
    this.userTerm = '';
    this.userResults.set([]);
  }

  addGroup(group: PersonRef): void {
    this.labels.set(group.id, group.displayName);
    this.editing.update((d) => (d ? { ...d, groups: [...new Set([...d.groups, group.id])] } : d));
    this.groupTerm = '';
    this.groupResults.set([]);
  }

  removeUser(id: string): void {
    this.editing.update((d) => (d ? { ...d, users: d.users.filter((u) => u !== id) } : d));
  }

  removeGroup(id: string): void {
    this.editing.update((d) => (d ? { ...d, groups: d.groups.filter((g) => g !== id) } : d));
  }

  label(id: string): string {
    return this.labels.get(id) ?? id;
  }

  // ---- save & delete -----------------------------------------------------

  /** Whether the draft is a saved policy — only those may be deleted. */
  isSaved(policy: MenuPolicy): boolean {
    return !this.isBuiltin(policy) && this.menuPerms.policies().some((p) => p.id === policy.id);
  }

  /** A policy needs a name, someone it belongs to, and a menu it opens. */
  isValid(): boolean {
    const d = this.editing();
    return !!d && d.name.trim().length > 0 && d.menus.length > 0 && d.users.length + d.groups.length > 0;
  }

  save(): void {
    const draft = this.editing();
    if (!draft || !this.isValid()) {
      return;
    }
    const list = this.menuPerms.policies();
    const next = list.some((p) => p.id === draft.id) ? list.map((p) => (p.id === draft.id ? draft : p)) : [...list, draft];
    this.persist(next);
  }

  remove(): void {
    const draft = this.editing();
    if (!draft || this.isBuiltin(draft)) {
      return;
    }
    this.persist(this.menuPerms.policies().filter((p) => p.id !== draft.id));
  }

  private persist(list: MenuPolicy[]): void {
    this.busy.set(true);
    this.menuPerms
      .save(list)
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        this.busy.set(false);
        if (result === null) {
          this.notification.showError(this.translate.instant('DMS.MENUPERM.SAVE_FAILED'));
          return;
        }
        this.editing.set(null);
        this.notification.showInfo(this.translate.instant('DMS.MENUPERM.SAVED'));
      });
  }
}
